// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';

// Global imports
import theme from './theme';
import AppLayout from './components/AppLayout';
import NavigationBar from './components/NavigationBar';
import { isAuthenticated, clearAuth } from './utils/auth';

// Auth modules
import Login from './modules/auth/Login';
import LoginAudit from './modules/auth/LoginAudit';
import AuditMaster from './modules/auth/AuditMaster';

// ESS modules
import Dashboard from './modules/ess/Dashboard';
import MyProfile from './modules/ess/MyProfile';
import ConnectPlus from './modules/ess/ConnectPlus';
import CommunicationCenter from './modules/ess/CommunicationCenter';
import AdminDocumentPortal from './modules/ess/AdminDocumentPortal';

// Employee modules
import EmployeeMaster from './modules/employee/EmployeeMaster';
import AddEmployee from './modules/employee/AddEmployee';
import EditEmployee from './modules/employee/EditEmployee';
import EmployeeProfile from './modules/employee/EmployeeProfile';

// Leave modules
import LeaveDashboard from './modules/leave/LeaveDashboard';
import LeaveMaster from './modules/leave/LeaveMaster';

// Attendance modules
import AttendanceDashboard from './modules/attendance/AttendanceDashboard';
import AttendanceMaster from './modules/attendance/AttendanceMaster';

// Meeting module (NEW)
import MeetingManagement from './modules/meeting/MeetingManagement';

// Roster module
import RosterDashboard from './modules/roster/RosterDashboard';

// Payroll module
import PayrollDashboard from './modules/payroll/PayrollDashboard';

// Settings modules
import MasterSettings from './modules/settings/MasterSettings';
import SuperAdminSettings from './modules/settings/SuperAdminSettings';

// NEW: Public Document Upload Page
import DocumentUploadPage from './modules/public/DocumentUploadPage';

import ChatbotApplet from './components/ChatbotApplet';

const AuthRedirect = () => {
  return isAuthenticated() ? <Navigate to="/dashboard" /> : <Navigate to="/login" />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const role = localStorage.getItem('role');
  if (!role) return <Navigate to="/" />;
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/dashboard" />;
  return children;
};

const ShellWrapper = ({ children }) => {
  const location = useLocation();
  // THE FIX: Added bypass logic for the new public upload route
  const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname.startsWith('/upload-documents');
  
  if (isPublicPage) return <>{children}</>;
  return <AppLayout topbar={<NavigationBar />}>{children}</AppLayout>;
};

function App() {
  useEffect(() => {
    let timeoutId;
    
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (localStorage.getItem('role')) {
          clearAuth();
          alert("Session expired due to inactivity. Please log in again.");
          window.location.href = '/';
        }
      }, 600 * 60 * 1000); // 10 hours
    };
    
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();
    
    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ShellWrapper>
          <Routes>
            <Route path="/" element={<AuthRedirect />} />
            <Route path="/login" element={<Login />} />
            
            {/* NEW: Public Unauthenticated Route */}
            <Route path="/upload-documents/:token" element={<DocumentUploadPage />} />
            
            {/* ESS Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/my-profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
            <Route path="/connect" element={<ProtectedRoute><ConnectPlus /></ProtectedRoute>} />
            <Route path="/messages/news" element={<ProtectedRoute><CommunicationCenter channel="news" title="Company News" /></ProtectedRoute>} />
            <Route path="/messages/announcements" element={<ProtectedRoute><CommunicationCenter channel="announcements" title="Company Announcements" /></ProtectedRoute>} />
            <Route path="/messages/notifications" element={<ProtectedRoute><CommunicationCenter channel="notifications" title="System Notifications" /></ProtectedRoute>} />
            
            {/* Employee Routes */}
            <Route path="/employees" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><EmployeeMaster /></ProtectedRoute>} />
            <Route path="/employees/add" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><AddEmployee /></ProtectedRoute>} />
            <Route path="/employees/edit/:id" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><EditEmployee /></ProtectedRoute>} />
            <Route path="/employee/:id" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><EmployeeProfile /></ProtectedRoute>} />
            <Route path="/hr/documents" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><AdminDocumentPortal /></ProtectedRoute>} />
            
            {/* Leave Routes */}
            <Route path="/leave" element={<ProtectedRoute><LeaveDashboard /></ProtectedRoute>} />
            <Route path="/leave-master" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager', 'HR Executive']}><LeaveMaster /></ProtectedRoute>} />
            
            {/* Attendance Routes */}
            <Route path="/attendance" element={<ProtectedRoute><AttendanceDashboard /></ProtectedRoute>} />
            <Route path="/attendance-master" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager']}><AttendanceMaster /></ProtectedRoute>} />
            
            {/* Meeting Management Route */}
            <Route path="/meeting" element={<ProtectedRoute><MeetingManagement /></ProtectedRoute>} />

            {/* Other Routes */}
            <Route path="/roster" element={<ProtectedRoute><RosterDashboard /></ProtectedRoute>} />
            <Route path="/payroll" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin', 'HR Manager']}><PayrollDashboard /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin']}><MasterSettings /></ProtectedRoute>} />
            <Route path="/superadmin" element={<ProtectedRoute allowedRoles={['Super Admin']}><SuperAdminSettings /></ProtectedRoute>} />
            <Route path="/login-audit" element={<ProtectedRoute allowedRoles={['Admin', 'Super Admin']}><AuditMaster /></ProtectedRoute>} />
          </Routes>
        </ShellWrapper>
      </Router>
    </ThemeProvider>
  );
}

export default App;